import { useCallback, useEffect, useMemo, useState } from 'react';
import { getConfig } from '../../../config';
import { AdminApiDomain, getAdminApiUrl } from '../../../config/ApiRouting';
import {
  BridgeTenantClient,
  type BridgeTenant,
  type BridgeTenantStatus,
  type RegisterBridgeTenantInput,
} from '../../../infrastructure/BridgeTenantClient';

interface UseBridgeTenants {
  tenants: BridgeTenant[];
  loading: boolean;
  error: string | null;
  register: (input: RegisterBridgeTenantInput) => Promise<BridgeTenant>;
  setStatus: (tenantId: string, status: BridgeTenantStatus) => Promise<void>;
  remove: (tenantId: string) => Promise<void>;
}

/**
 * Loads and edits the bridge tenant registry.
 *
 * Mutations apply the row the server just returned rather than refetching the list. A refetch
 * would make the operator wait for a second round trip before the handoff dialog opens, blank the
 * table while it runs, and — worst — let a failed GET after a successful POST report itself as an
 * empty registry.
 */
export const useBridgeTenants = (): UseBridgeTenants => {
  const config = getConfig();
  const client = useMemo(
    () => new BridgeTenantClient(getAdminApiUrl(AdminApiDomain.BRIDGE_TENANT), config.apiKey),
    [config.apiKey],
  );

  const [tenants, setTenants] = useState<BridgeTenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setTenants(await client.list());
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : '브랜드 목록을 불러오지 못했습니다');
      setTenants([]);
    } finally {
      setLoading(false);
    }
  }, [client]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const register = useCallback(
    async (input: RegisterBridgeTenantInput) => {
      const created = await client.create(input);
      setTenants((current) => [...current, created]);
      return created;
    },
    [client],
  );

  const setStatus = useCallback(
    async (tenantId: string, status: BridgeTenantStatus) => {
      const updated = await client.updateStatus(tenantId, status);
      setTenants((current) => current.map((tenant) => (tenant.tenantId === updated.tenantId ? updated : tenant)));
    },
    [client],
  );

  const remove = useCallback(
    async (tenantId: string) => {
      await client.remove(tenantId);
      setTenants((current) => current.filter((tenant) => tenant.tenantId !== tenantId));
    },
    [client],
  );

  return { tenants, loading, error, register, setStatus, remove };
};
